import type { ReactNode } from "react";
import { GripVertical, X } from "lucide-react";

import { cn } from "../../../lib/cn";

export type WidgetChromeProps = {
  title: string;
  editMode: boolean;
  onRemove: () => void;
  children: ReactNode;
};

// Wraps a rendered widget so edit-mode adds the drag handle, remove
// button, and resize affordance without polluting the widget bodies.
// The .widget-drag-handle class is matched by react-grid-layout's
// `draggableHandle` prop -- drag is only initiated from this corner so
// pointer interactions inside the widget (tab clicks, links) still work.
export function WidgetChrome({
  title,
  editMode,
  onRemove,
  children,
}: WidgetChromeProps) {
  return (
    <div
      className={cn(
        "relative flex h-full w-full flex-col",
        editMode && "ring-1 ring-outline-variant",
      )}
      data-widget-edit-mode={editMode ? "true" : undefined}
    >
      {editMode && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-2 px-1 py-1">
          <button
            type="button"
            aria-label={`Drag ${title}`}
            className="widget-drag-handle pointer-events-auto inline-flex h-6 w-6 cursor-grab items-center justify-center rounded-sm bg-surface-container-high text-on-surface-variant shadow-sm hover:text-on-surface active:cursor-grabbing"
            onClick={(event) => {
              // The handle is for drag pointer events; click is a no-op.
              event.preventDefault();
            }}
          >
            <GripVertical className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={`Remove ${title}`}
            className="pointer-events-auto inline-flex h-6 w-6 items-center justify-center rounded-sm bg-surface-container-high text-on-surface-variant shadow-sm hover:bg-danger hover:text-on-danger"
            onClick={onRemove}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
      <div className="h-full w-full">{children}</div>
    </div>
  );
}
